<template>

  <v-container fluid class="provider-order-details-preview">

    <!-- Title and close action -->
    <v-row class="mt-1">
      <v-col cols="12" xs="12" sm="9" md="9" lg="9">
        <div style="font-size:1.5rem;">
          {{ providerNameForDisplay }} Order: {{ orderNumber }}
        </div>
      </v-col>
      <v-col cols="12" xs="12" sm="3" md="3" lg="3" align="right">
        <a class="pr-4" style="font-size:16px;" @click="closePreview">Close</a>
      </v-col>
    </v-row>

    <!-- Loaded provider details -->
    <v-container fluid class="mt-8 page-content">

      <!-- Provider invoice link, order total, brand name -->
      <v-row class="pt-0 mt-0">
        <v-col cols="12" xs="12" sm="6" md="6" lg="6">
          <div v-if="!isDataLoading && providerInvoiceUrl">
            <a :href="providerInvoiceUrl" :title="providerInvoiceUrl" target="_new">{{ providerNameForDisplay }} Invoice</a>
            <v-icon class="ml-2" style="font-size:16px" color="primary">mdi-open-in-new</v-icon>
          </div>
          <div class="mt-6 mb-2 order-total" v-if="orderDetails">
            <span style="font-size:1.3rem;">Total: ${{ orderTotalForDisplay }}</span>
          </div>
        </v-col>
        <v-col cols="12" xs="12" sm="6" md="6" lg="6" align="center">
          <template v-if="!isDataLoading && brandName">
            <div style="font-size:14px;font-style:italic;padding-top:6px;padding-bottom:4px;">Brand:</div>
            <div style="font-size:1.2rem;color:#ffffff;">
              {{ brandName }}
            </div>
          </template>
        </v-col>
      </v-row>

      <!-- (data loading spinner) -->
      <v-container v-if="isDataLoading" class="d-flex flex-column align-center justify-center">
        <v-progress-circular indeterminate :size="50" style="margin-bottom:30px" color="primary" />
        <div style="font-size:1.18rem;">Loading order details from {{ providerNameForDisplay }}</div>
      </v-container>

      <!-- Ordered Items -->
      <div class="mt-2 ordered-items-container" v-else>
        <v-data-table v-if="orderedItems.length > 0"
            class="list-order-details"
            :headers="headers"
            :items="orderedItems"
            :footer-props="paginationOptions">

          <!-- line total -->
          <template v-slot:[`item.item_total`]="{ item }">
            <!-- (on sale) -->
            <template v-if="item.is_sale === true && !isCredit">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                      color="yellow darken-4"
                      style="cursor:pointer;font-size:21px;margin-right:5px;padding-bottom:3px;"
                      v-bind="attrs"
                      v-on="on">
                    mdi-tag
                  </v-icon>
                </template>
                <div>On sale: ${{ item.sale_price }} per case</div>
                <div>(Retail price: ${{ item.unit_price }})</div>
              </v-tooltip>
              <span>{{ formatTotal(item.item_total) }}</span>
            </template>
            <!-- (regular price) -->
            <template v-else>
              <span>{{ formatTotal(item.item_total) }}</span>
            </template>
          </template>
        </v-data-table>

        <div style="margin-top:184px;" v-else>
          (No ordered items)
        </div>
      </div> <!-- END ordered-items-container -->

    </v-container> <!-- END page-content -->

    <!-- Submitted Info -->
    <div class="mt-6 py-2 submitted-status-text" v-if="submissionRecord">
      <span style="font-style:normal;font-size:1.0rem;" class="pr-2">Submitted to {{ appName }}:</span>
      <span>{{ submittedDateForDisplay }}</span>
    </div>
    <!-- Ship Button (for order or credit memo) -->
    <div style="text-align:center;height:80px;" v-else>
      <template v-if="showShipButton && order && order.order_details">
        <v-btn
            v-if="order.order_details.total < 0"
            class="action-button mt-6"
            color="yellow darken-4"
            :disabled="shipper.shipper_status !== 'APPROVED'"
            @click="selectForCredit">
          {{ issueCreditText }}
        </v-btn>
        <v-btn
            v-else
            class="action-button mt-6"
            color="blue"
            :disabled="shipper.shipper_status !== 'APPROVED'"
            @click="selectForDelivery">
          {{ shipButtonText }}
        </v-btn>
      </template>
    </div>

  </v-container>

</template>

<script>
export default {
  name: 'ProviderOrderDetailsPreview',

  props: {
    orderId: {
      required: true
    },
    orderNumber: {
      required: true
    },
    providerAlias: {
      required: true
    },
    shipper: {
      required: true
    },
    accountId: {
      default: null
    },
    brandId: {
      default: null
    },
    brandName: {
      default: null
    }
  },

  data () {
    return {
      headers: [
        // { text: 'Product ID', value: 'product_id' },
        { text: 'Batch Number', value: 'batch_number' },
        { text: 'Description', value: 'product_label' },
        { text: 'Quantity', value: 'quantity' },
        { text: 'Total (USD)', value: 'item_total' }
      ],
      isDataLoading: false,
      paginationOptions: {
        'items-per-page-options': [10, 25, 50, 100]
      },
      isAlreadySubmitted: true,
      submissionRecord: null,
      order: null,
      orderDetails: null,
      orderedItems: []
    }
  },

  computed: {
    appName () {
      return this.$store.getters['app/platform']
    },
    providerNameForDisplay () {
      return this.$root.providerAliasToName(this.providerAlias)
    },
    showShipButton () {
      return !this.isAlreadySubmitted
    },
    // Ship Order action
    shipButtonText () {
      return 'Ship Order'
    },
    // Issue Credit action
    issueCreditText () {
      return 'Issue Credit'
    },
    isCredit () {
      return (this.orderDetails) ? this.orderDetails.total < 0 : false
    },
    orderTotalForDisplay () {
      return (this.orderDetails) ? Number(this.orderDetails.total).toLocaleString() : ''
    },
    submittedDateForDisplay () {
      return (this.submissionRecord) ? this.$root.utils.dateFormat(this.submissionRecord.created_at, 'YYYY MMM D h:mma z') : ''
    },
    activeProviderConnection () {
      return this.$store.getters['sh/providerConnection'](this.providerAlias, this.accountId)
    },
    providerInvoiceUrl () {
      return (this.activeProviderConnection)
        ? this.$root.providerInvoiceUrl(this.order, this.activeProviderConnection)
        : null
    }
  },

  watch: {
    // Reload data when order changes
    orderId: function (updated) {
      if (updated) this.loadData()
    }
  },

  beforeMount () {
    this.loadData()
  },

  beforeDestroy () {
    this.clearPreview()
  },

  methods: {
    // Emit close action
    closePreview () {
      this.$emit('close')
      this.clearPreview()
    },

    // Clear loaded data
    clearPreview () {
      this.isAlreadySubmitted = true
      this.submissionRecord = null
      this.order = null
      this.orderDetails = null
      this.orderedItems = []
    },

    // Display formatted total
    formatTotal (total) {
      const totalAsNum = total || 0
      return `$${Number(totalAsNum).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
    },

    // Load submit page for a credit
    selectForCredit () {
      if (this.order) {
        this.$root.goTo('provider-order-issue-credit', {
          orderId: this.order.thirdparty_order_id,
          providerAlias: this.order.provider_alias,
          accountId: this.accountId,
          brandId: this.brandId
        })
      }
    },

    // Load submit page for an order
    selectForDelivery () {
      if (this.order) {
        this.$root.goTo('provider-order-submit', {
          orderId: this.order.thirdparty_order_id,
          providerAlias: this.order.provider_alias,
          accountId: this.accountId,
          brandId: this.brandId
        })
      }
    },

    async loadData () {
      this.isDataLoading = true

      // Check if order submitted
      try {
        this.submissionRecord = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `provider/order/${this.orderId}/status`,
          opts: {
            bodyData: {
              provider: this.providerAlias,
              account_id: this.accountId
            }
          }
        })

        if (this.submissionRecord.status === 'SUBMITTED') this.isAlreadySubmitted = true
      } catch (err) {
        this.isAlreadySubmitted = false
      }

      // Load order item with details
      if (this.orderId && this.providerAlias) {
        this.order = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `provider/order/${this.orderId}`,
          opts: {
            params: {
              provider: this.providerAlias,
              account_id: this.accountId,
              brand_id: this.brandId
            }
          }
        })
        this.orderDetails = this.order.order_details
        this.orderedItems = (this.orderDetails) ? this.orderDetails.item_data.items : []
      }

      this.isDataLoading = false
    }
  }
}
</script>

<style>
  .provider-order-details-preview .ordered-items-container {
    text-align: center;
    max-height: 400px;
    overflow-y: scroll;
  }

  .provider-order-details-preview .action-button {
    height: 52px !important;
    width: 140px;
  }

  .provider-order-details-preview .submitted-status-text {
    font-size: 1.1rem;
    text-align: center;
    color: #727272;
    margin: 0 auto;
  }
</style>
